import React, { useMemo } from "react";
import { useObserver } from "mobx-react";
import { Paper, Typography, makeStyles } from "@material-ui/core";
import { FileExcel, Email, Comment } from "mdi-material-ui";
import FaceIcon from "@material-ui/icons/Face";
import DeleteIcon from "@material-ui/icons/Delete";
import { Topbar } from "@components";
import TextEditorDialog from "@components/TextEditorDialog";
import { ContactListItemFragment } from "@graphql";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { useUserProfile } from "@hooks";
import {
  MassContactActionsStore,
  MassContactActionsStoreOptions
} from "./MassContactActionsStore";
import { ContactList, ContactListColumn } from "./components/ContactList";
import { ActionBar, ActionBarButton } from "./components/ActionBar";
import { TrunqWarning } from "./components/TrunqWarning";

type Props = {
  columns: ContactListColumn[];
  options: MassContactActionsStoreOptions;
};

export function MassContactActions(props: Props) {
  const { columns, options } = props;

  const store = useMemo(() => new MassContactActionsStore(options), [options]);
  const classes = useStyles({});
  const { can } = useUserProfile();

  const itemCount = (predicate: (x: ContactListItemFragment) => boolean) => {
    return store.selectedItems.any()
      ? ` (${store.selectedItems.filter(predicate).length}/${store.selectedItems.length})`
      : "";
  };

  const renderActionBar = () => {
    const buttons: ActionBarButton[] = [
      {
        disabled: !store.canSMS || !store.anySelected,
        title: locale.SMS + itemCount((x) => x.CellPhoneValid),
        icon: <Comment />,
        onClick: store.openSMSDialog,
        tooltip: !store.canSMS ? "You don't have a mobile number assigned" : undefined
      }
    ];

    if (can("mass-actions:export:all")) {
      buttons.push({
        disabled: !store.isExcelDownloadEnabled,
        loading: store.isDownloading,
        onClick: store.getCsv,
        title: locale.Excel,
        icon: <FileExcel />
      });
    }

    if (columns.includes("Email")) {
      buttons.push({
        disabled: !store.anySelected,
        title: locale.Email + itemCount((x) => x.EmailValid),
        icon: <Email />,
        // onClick: store.openMailDialog
        onClick: store.mailSelected
      });
    }

    if (columns.includes("ResponsiblePerson") && can("consultant:update-ba-bulk:all")) {
      buttons.push({
        disabled: !store.anySelected,
        title: `${locale.ChangeBA} ${itemCount((x) => Boolean(x))}`,
        icon: <FaceIcon />,
        onClick: store.updateResponsiblePerson,
        ButtonProps: {
          "data-test-id": "change-responsible-person"
        }
      });
    }
    if (columns.includes("Delete")) {
      buttons.push({
        disabled:
          !store.canDeleteConsultants ||
          !store.anySelected ||
          !store.isSelectedOnlyUnassigned,
        title: locale.Delete + itemCount((x) => Boolean(x)),
        icon: <DeleteIcon />,
        tooltip: !store.isSelectedOnlyUnassigned
          ? "Du kan enbart ta bort konsulter som saknar Ansvarig BA."
          : null,
        onClick: store.deleteItems,
        ButtonProps: {
          "data-test-id": "change-responsible-person"
        }
      });
    }

    return (
      <Paper className={classes.actions}>
        <ActionBar
          buttons={buttons}
          onlyMyDisabled={store.onlyMyDisabled}
          onlyMy={store.onlyMy}
          onOnlyMy={(v) => (store.onlyMy = v)}
          workRequestMailing={store.isWorkRequestMailing}
          onWorkRequestMailing={(e, v) => store.setWorkRequestMailing(v)}
          campaign={store.isCampaign}
          onCampaign={(e, v) => store.setCampaign(v)}
        />
      </Paper>
    );
  };

  return useObserver(() => (
    <div className={classes.root} data-test="MassContactActions">
      <Topbar className={classes.topbar}>
        <Typography variant="h6">{locale.MassActions}</Typography>
      </Topbar>

      {store.isTruncated && (
        <TrunqWarning limit={store.items.length} total={store.total} />
      )}

      {renderActionBar()}

      <ContactList
        columns={props.columns}
        className={classes.list}
        loading={store.isLoading}
        items={store.itemsWithUnavailable.slice()}
        selectedIds={store.selectedIds.slice()}
        smsResult={store.sendSmsResult}
        onSelect={(item) => store.toggleItem(item._id)}
        onSelectAll={store.toggleAll}
        isAllSelected={store.allSelected}
        isSelectAllDisabled={!store.hasSelectableItems}
        dropdownCheckboxOptions={[
          { label: locale.None, onClick: store.selectNone },
          { label: locale.OnlyMy, onClick: store.selectMy },
          { label: locale.Unassigned, onClick: store.selectUnassigned },
          { label: locale.MyUnassigned, onClick: store.selectMyAndUnassigned },
          { label: locale.All, onClick: store.selectAll }
        ]}
      />

      <TextEditorDialog
        open={store.isTextEditorOpen}
        initialValues={store.textEditorInitialValues}
        TextEditorProps={{
          isFromReadonly: true,
          showTo: false
        }}
        onClose={store.closeTextEditor}
        onSubmit={store.textEditorSubmit}
        parseTemplate={store.parseTemplate}
        templates={[]}
      />
    </div>
  ));
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    maxHeight: "100%",

    "& > *:not(:last-child)": {
      marginBottom: theme.spacing()
    }
  },

  topbar: {
    flex: "0 0 auto"
  },

  actions: {
    flex: "0 0 0"
    // marginBottom: theme.spacing(2),
    // marginTop: theme.spacing(2)
  },

  list: {
    overflowY: "auto"
  }
}));
